import { differenceInDays } from 'date-fns';
import { getAttributeId, getAttributeValue, isPrimeurCode } from './productUtils';
import { BadgeBackgroundColor } from '@/types';
import { STORE_ID } from '@/constants/stores';

export const availableInDays = (availabilityDate) => {
  return (availabilityDate instanceof Date) && !isNaN(availabilityDate) ?
    differenceInDays(availabilityDate, new Date()) : 0;
};

export const getAvailabilityDate = (product) => {
  const productMillesime = getAttributeValue(product, 'millesime');

  // For primeur wineboxes, use the first item to determine the vintage
  if (productMillesime === undefined && product.products?.length) {
    const [firstWineboxProduct] = product.products;
    const primeurMillesime = getAttributeValue(firstWineboxProduct.product, 'millesime');
    return Number(primeurMillesime) + 3;
  }
  return Number(productMillesime) + 3;
};

export const isPrimeur = (t, product) => {
  const date = product && getAvailabilityDate(product);
  return (
    product &&
      isPrimeurCode(getAttributeId(product, 'in_stock')) &&
      !Number.isNaN(date) ? (
        {
          color: BadgeBackgroundColor.BROWN,
          message: t('product.availability.P', { date }),
          isInCellar: true
        }
      ) : null
  );
};

export const isPreArrivals = (t, currentItem, storeId) => {
  if (!storeId ||
    ![STORE_ID.SINGAPORE, STORE_ID.USA].includes(storeId) ||
    !(getAttributeId(currentItem, 'prearrival_o_n') === '1')
  ) {
    return null;
  }

  const baseResult = {
    color: BadgeBackgroundColor.YELLOW,
    isInCellar: true,
    message: t('product.availability.PA')
  };

  if ([STORE_ID.USA].includes(storeId)) {
    return baseResult;
  }

  if ([STORE_ID.SINGAPORE].includes(storeId)) {
    const availabilityDate = new Date(currentItem?.availability);
    const days = availableInDays(availabilityDate);

    if (days <= 1) {
      return {
        ...baseResult,
        message: t('product.availability.PA_available')
      };
    }

    return {
      ...baseResult,
      isInCellar: false,
      message: t('product.availability.PA_unavailable', {
        date: new Intl.DateTimeFormat('en-SG', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }).format(availabilityDate).toString()
      })
    };
  }

  return null;
};

export const isInStock = (t, currentItem) =>
  currentItem && currentItem.brokerageFiveDay ? (
    {
      color: BadgeBackgroundColor.INFORMATION,
      message: t('product.availability.brokerageFiveDay'),
      isInCellar: true
    }
  ) :
    currentItem && (getAttributeId(currentItem, 'inStockShop') === '1') ? (
      {
        color: BadgeBackgroundColor.SUCCESS,
        message: t('product.availability.inStockShop'),
        isInCellar: true
      }
    ) : null;

export const isAvailable = (t, locale, currentItem) => {
  const availabilityDate = new Date(currentItem?.availability);
  const days = availableInDays(availabilityDate);
  return days <= 1 ? (
    {
      color: BadgeBackgroundColor.SUCCESS,
      message: t('product.availability.available'),
      isInCellar: true
    }
  ) : (
    {
      color: BadgeBackgroundColor.YELLOW,
      message: t('product.availability.unavailable', {
        date: availabilityDate.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' })
      }),
      isInCellar: false
    }
  );
};

export const getAvailabilityIndicator = (t, storeId, product, currentItem, locale) => {
  const availabilities = [
    (
      isPrimeur(t, product) ||
      isInStock(t, currentItem) ||
      isPreArrivals(t, currentItem, storeId) ||
      isAvailable(t, locale, currentItem)
    )
  ].filter(Boolean);
  // The filter(Boolean) here is used in prevision of futures cases outside of the parentheses cases
  // when a product can have multiple availabilityIndicator
  return availabilities;
};
